/**
 * Professor Ka on the marketing pages — what the landing and the About page
 * share: the timing vocabulary Ka moves with, the size model and face of the
 * one floating Ka (the "Ka floating" Rive icon), and the desk Ka (Rive) that
 * takes over from it where Ka sits down (the landing's close, his own card on
 * About).
 */
import {
  animate,
  motion,
  type MotionStyle,
  type MotionValue,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';
import { lazy, Suspense, useRef, useState, useSyncExternalStore } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  KA_FLOATING_CANVAS_BOX,
  kaFloatingDrop,
} from '@/components/rive/ka-floating-frame';
import { cn } from '@/lib/utils';

import { useIdleSoon } from './landing-idle';

/** Crisp decelerate curve for every triggered reveal on the pages. */
export const EASE_OUT = [0.22, 1, 0.36, 1] as const;
/** Ka's travel between its resting corner and a stage position. Time-based
 *  (not scrubbed), so scrolling never drags Ka backwards. */
export const KA_GLIDE = { type: 'spring', stiffness: 240, damping: 28 } as const;
/** Ka floating from one spot to the next (user 2026-09-24: "a smooth floating
 *  transition"): an ease-in-out drift, not a spring — a spring covers most of
 *  the way in its first third, which reads as a dash. Callers set the duration
 *  per float from its length. */
export const KA_FLOAT = {
  type: 'tween',
  ease: [0.45, 0, 0.3, 1],
  duration: 1.1,
} as const;

/**
 * The floating Ka's box — his CARD — is laid out at its LARGEST pose, 120px,
 * and scaled DOWN to 64px for the corner poses. Not the other way round: the
 * canvas renders at its laid-out size, so a small box scaled up by transform
 * stretches a small bitmap and Ka goes soft exactly where he is biggest.
 * Downscaling stays crisp.
 */
export const KA_BOX = 120;
export const KA_SMALL = 64;
export const KA_SMALL_SCALE = KA_SMALL / KA_BOX;

// ── The floating Ka (Rive "Ka floating") ─────────────────────────────────────

/** The icon: the Rive runtime and its wasm load lazily, never in a page's
 *  entry chunk. */
const KaFloatingRive = lazy(() => import('@/components/rive/ka-floating-rive'));

/**
 * The one floating Ka's face (user 2026-09-27: the Rive icon instead of the
 * dotLottie mascot), filling the travelling Ka's 120px box: Ka in his card
 * follows the cursor and, on his own, writes behind his paper or peeks out of
 * the frame. Fades in once the file has loaded. Click-through, like the box.
 */
export function FloatingKa({
  reduceMotion,
  box = KA_BOX,
}: {
  reduceMotion: boolean;
  /** The laid-out card size (the travelling Ka's 120; the phone layout's own
   *  Ka is smaller), for the drop shadow's scale. */
  box?: number;
}) {
  const [ready, setReady] = useState(false);
  // The Rive runtime + wasm (~0.5 MB) start on the first idle moment, not
  // with the hero: Ka fades in once drawn either way.
  const start = useIdleSoon();
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute transition-opacity duration-300"
      style={{
        ...KA_FLOATING_CANVAS_BOX,
        filter: kaFloatingDrop(box),
        opacity: ready ? 1 : 0,
      }}>
      {/* Ka is decoration: a chunk that fails to load (a flaky line, a tab
          open across a deploy) leaves his card empty, never the page. */}
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          {start && (
            <KaFloatingRive
              followCursor={!reduceMotion}
              still={reduceMotion}
              onReady={() => setReady(true)}
              className="h-full w-full"
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

// ── Where Ka has landed ──────────────────────────────────────────────────────

/**
 * The slot the floating Ka has LANDED on (its `data-lv3-ka` id), or null —
 * set by whichever layout's Ka is travelling (desktop `KaTravel`, the phone's
 * `PhoneKa`) once a glide onto a slot has finished, or at once when he fades
 * in on it. A block speaks when Ka gets there (user 2026-09-30: the agent
 * chat's "Want to be more specific?" streams from him as he arrives).
 */
let kaLanded: string | null = null;
const kaLandedListeners = new Set<() => void>();

export function setKaLanded(id: string | null) {
  if (id === kaLanded) return;
  kaLanded = id;
  kaLandedListeners.forEach((listener) => listener());
}

/** True while Ka rests on slot `id`. */
export function useKaLandedOn(id: string): boolean {
  return useSyncExternalStore(
    (listener) => {
      kaLandedListeners.add(listener);
      return () => kaLandedListeners.delete(listener);
    },
    () => kaLanded === id,
    () => false,
  );
}

/**
 * The feature cards that draw Ka themselves (the oral exam's examiner) while
 * he is on screen in them. While any is, the floating Ka steps out: never two
 * Kas at once. Each deck (desktop's, the phone's pile) reports its own card.
 */
const kaInCards = new Set<string>();
const kaInCardListeners = new Set<() => void>();

export function setKaInCard(cardId: string, shown: boolean) {
  if (shown === kaInCards.has(cardId)) return;
  if (shown) kaInCards.add(cardId);
  else kaInCards.delete(cardId);
  kaInCardListeners.forEach((listener) => listener());
}

/** True while a feature card shows its own Ka. */
export function useKaInCard(): boolean {
  return useSyncExternalStore(
    (listener) => {
      kaInCardListeners.add(listener);
      return () => kaInCardListeners.delete(listener);
    },
    () => kaInCards.size > 0,
    () => false,
  );
}

/** Feature cards whose media is Prof. Ka himself. */
export const CARDS_WITH_KA: ReadonlySet<string> = new Set(['oralExam']);

/**
 * Where Ka sits when a block is on screen: an empty box the size of the pose,
 * laid out in the block's flow. The page reads these rectangles (`kaSlotAt`)
 * and glides the one fixed Ka between them. `over`: the slot sits inside a
 * card (the agent chat), so Ka is drawn over the page there, not under it.
 */
export function KaSlot({
  id,
  size,
  over = false,
}: {
  id: string;
  size: number;
  over?: boolean;
}) {
  return (
    <div
      data-lv3-ka={id}
      data-lv3-ka-over={over || undefined}
      aria-hidden
      style={{ width: size, height: size }}
    />
  );
}

// ── Ka at his desk (Rive) ────────────────────────────────────────────────────

/** Ka at his desk: the Rive runtime and its wasm load only when a desk Ka
 *  comes near, never in a page's entry chunk. */
const LandingKaRive = lazy(() => import('./landing-ka-rive'));

/** The desk Ka's artboard (320×240). Draw him at this size or smaller — the
 *  canvas renders at its display size, never scaled up. */
export const DESK_KA = { w: 320, h: 240 } as const;
/** Centre of Ka's head in the artboard (measured off the render) — where the
 *  floating Ka hands over. */
const DESK_KA_HEAD = { x: 157, y: 96 } as const;
/** The artboard's headroom over Ka's head that the landing's frame crops (its
 *  box is 216 tall, desk at the bottom). */
export const DESK_KA_HEADROOM = 24;

/**
 * The cursor-following Rive Ka at his desk (user 2026-09-25) where he stands
 * in for the floating Ka — the two are never on screen together. The floating
 * Ka arrives on the empty box over this one's head (the `[data-lv3-ka]` slot,
 * 120px on a 320px desk, so it keeps the pose's size at any width); the page
 * then sets `shown`, the floating Ka fades out and this one fades in, in the
 * same spot. Size the box with `className` / `style` at 4:3.
 */
export function DeskKa({
  shown,
  reduceMotion,
  slotId,
  className,
  style,
}: {
  shown: MotionValue<number>;
  reduceMotion: boolean;
  /** The hand-over slot's `data-lv3-ka` id. */
  slotId: string;
  className?: string;
  style?: MotionStyle;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Mounted a screen and a half early, so the file is parsed and Ka drawn
  // before the reader reaches him.
  const near = useInView(ref, { once: true, margin: '1500px 0px' });
  const opacity = useMotionValue(shown.get());
  // Invisible, he takes no clicks: a poke on an empty box would be a ghost.
  const pointerEvents = useTransform(opacity, (o) => (o < 0.5 ? 'none' : 'auto'));
  useMotionValueEvent(shown, 'change', (v) => {
    animate(
      opacity,
      v,
      reduceMotion
        ? { duration: 0 }
        : // In a beat after the floating Ka starts to fade; out at once.
          v
          ? { delay: 0.15, duration: 0.3, ease: EASE_OUT }
          : { duration: 0.18, ease: EASE_OUT },
    );
  });
  return (
    <motion.div
      ref={ref}
      style={{ ...style, opacity, pointerEvents }}
      className={cn('relative', className)}>
      {near && (
        // As the floating Ka: a failed chunk leaves the desk empty.
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <LandingKaRive followCursor={!reduceMotion} still={reduceMotion} />
          </Suspense>
        </ErrorBoundary>
      )}
      {/* The hand-over slot, click-through: it sits over his head, where a
          click or tap pokes him. */}
      <div
        data-lv3-ka={slotId}
        aria-hidden
        className="pointer-events-none absolute aspect-square"
        style={{
          left: `${((DESK_KA_HEAD.x - KA_BOX / 2) / DESK_KA.w) * 100}%`,
          top: `${((DESK_KA_HEAD.y - KA_BOX / 2) / DESK_KA.h) * 100}%`,
          width: `${(KA_BOX / DESK_KA.w) * 100}%`,
        }}
      />
    </motion.div>
  );
}
